import { Injectable } from '@angular/core';
import { Workbook, Cell, Worksheet, Row } from 'exceljs';
import { saveAs } from 'file-saver';
import { SESSION_KEYS } from 'src/app/dataModels/session-keys';
import { SessionService } from 'src/app/services/session/session.service';
import { HelperService } from 'src/app/services/utils/helper.service';

/**
 * Export to Excel
 */
@Injectable({
  providedIn: 'root'
})
export class ExcelService {
  /**
   * constructor to inject dependency
   * @param helperService instance of helperService
   */
  constructor(private helperService: HelperService, private sessionService: SessionService) {}

  /**
   * export data to excel file
   * @param title title of the excel
   * @param data data to be exported
   * @param headers headers for the data
   * @param additionalData additional data to be included above the data
   * @param subTitle subTitle for the excel
   * @param fileName fileName for the excel to be downloaded
   */
  exportTableToExcel(
    title: string,
    data: any[],
    headers: any[],
    additionalData: any[],
    subTitle: string,
    fileName?: string,
    centerAlignAdditionalData?
  ) {
    // create workbook
    const workbook = new Workbook();

    const user = this.sessionService.getSessionData(SESSION_KEYS.USER);

    // create worksheet
    const worksheet = workbook.addWorksheet();

    // header row with 'text' attribute from the headers
    const headerRow = [];

    // width for each column
    let widthRow = [];

    let totalwidthRow = 0;

    // for header list, create headerRow and mergedCells data
    headers.forEach((h, i) => {
      if (this.helperService.isHeaderPartOfExport(h)) {
        headerRow.push(h.text);
        widthRow.push(h.text.length);
       //totalwidthRow = totalwidthRow + h.text.length + 5;
      }
    });
    

        // data row for the excel
        const dataRow = [];
        let counter = 0;
        for (const d of data) {
          const cells = [];
          let i = 0;
          headers.forEach(h => {
            if (this.helperService.isHeaderPartOfExport(h)) {
              const cellValue =
                h.format === 'PHONE'
                  ? this.helperService.formatPhone(d[h.field])
                  : d[h.field]
                      ?.toString()
                      .replace(/\n/g, '\r\n')
                      .replace(/<br\/>/g, '\n');
              cells.push(cellValue);
              if (cellValue) {
                const existingWidth = widthRow[i];
                widthRow[i] = existingWidth > cellValue.length ? existingWidth : cellValue.length;
                if(i == 0){
                  counter ++;
                }
                if(counter == 1) {
                totalwidthRow = totalwidthRow + widthRow[i] + 5;
                }
              }
              i++;
            }
          });
          dataRow.push(cells);
        }
    


    const numberOfColumns = this.getNumberOfColumns(headerRow, additionalData);
      // Define column widths (for example)
  
  const leftText = this.helperService.formatDate(new Date(), true);
  const rightText = `Printed By : ${user.lastName}, ${user.firstName}`;

  // Calculate total width of the columns
  const space = '  '.repeat(totalwidthRow - (leftText.length + rightText.length));
  const combinedHeader = `${leftText}${space}${rightText}`;

    let rowNumber = 1;

    this.addHeader(worksheet, numberOfColumns, combinedHeader, rowNumber, 10);

    rowNumber++;
    // add default title
    this.addHeader(worksheet, numberOfColumns, 'New Jersey Immunization Information System', rowNumber, 10);
    rowNumber++;
    this.addHeader(worksheet, numberOfColumns, 'Admin Facility', rowNumber, 10);

    rowNumber++;

    // add title for the excel
    if (!this.helperService.IsNullOrEmpty(title)) {
      this.addHeader(worksheet, numberOfColumns, title, rowNumber, 16);
      rowNumber++;
    }

    if (!this.helperService.IsNullOrEmpty(subTitle)) {
      this.addHeader(worksheet, headerRow.length, subTitle, rowNumber, 16);
      rowNumber++;
    }

    // add additional data
    const obj = this.addAdditionalData(worksheet, additionalData, rowNumber, numberOfColumns, widthRow, centerAlignAdditionalData);

    rowNumber = obj.rowNumber;

    widthRow = obj.widthRow;

    // Blank Row
    worksheet.addRow([]);

    // add header Row
    const cellHeader = worksheet.addRow(headerRow);

    // Cell Style : Fill and Border
    cellHeader.eachCell(cell => {
      cell.font = { bold: true, size: 10 };
      this.applyBackGroundToCell(cell, 'C6E0B4');
      this.applyBorderToCell(cell);
    });

        // set width to the column
        worksheet.columns.forEach((column, i) => {
          column.width = widthRow[i] + 2;
          totalwidthRow = column.width;
        });

    // add dataRow
    dataRow.forEach((d, i) => {
      const bgcolor = i % 2 === 0 ? 'EDEDED' : 'FFFFFF';
      worksheet.addRow(d).eachCell(c => {
        this.applyBackGroundToCell(c, bgcolor);
        this.applyBorderToCell(c);
        c.font = { size: 10 };
        c.alignment = { wrapText: true, vertical: 'middle' };
      });
    });

    // save file
    workbook.xlsx.writeBuffer().then(excelData => {
      const date = this.helperService.convertDate(new Date(), 'DD_MMM_YYYY_HH_mm_ss');
      const downloadFileName = fileName || 'Excel';
      const blob = new Blob([excelData], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, `${downloadFileName}_${date}.xlsx`);
    });
  }

  /**
   * appends header to the worksheet and apply formatting
   * @param worksheet worksheet to which the default title will be added
   * @param numberOfColumns number of columns to be merged
   * @param title title to be displayed
   * @param columnPosition starting column in the excel sheet
   * @param fontSize font size
   */
  private addHeader(
    worksheet: Worksheet,
    numberOfColumns: number,
    title: string,
    columnPosition: number,
    fontSize: number,
    alignment = 'center'
  ) {
    // add default title
    worksheet.addRow([title]);
    // merge cells
    worksheet.mergeCells(columnPosition, 1, columnPosition, numberOfColumns);

    const headerCell = worksheet.getCell(`A${columnPosition}`);

    this.applyFormatsToHeaderCell(headerCell, fontSize, alignment);
  }

  /**
   * apply formats to header cell
   * @param cell cell
   * @param fontSize font size
   */
  private applyFormatsToHeaderCell(cell: Cell, fontSize: number, alignment) {
    // align horizontally center
    cell.alignment = { horizontal: alignment };

    cell.font = { bold: true, size: fontSize };

    this.applyBorderToCell(cell);

    this.applyBackGroundToCell(cell, 'FFFFE0');
  }

  /**
   * fill the cell with background color
   * @param cell cell
   * @param bgColor background color
   */
  private applyBackGroundToCell(cell: Cell, bgColor: string) {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: bgColor },
      bgColor: { argb: bgColor }
    };
  }

  /**
   * apply thin border to the cell
   * @param cell cell
   */
  private applyBorderToCell(cell: Cell) {
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
  }

  /**
   * add additional data to worksheet
   * @param worksheet worksheet
   * @param additionalData additional Data to be added
   * @param rowNumber row number
   * @param numberOfColumns number of columns to merge
   * @param widthRow width
   * @param centerAlignAdditionalData indicate if additional data has to be center aligned
   */
  private addAdditionalData(
    worksheet: Worksheet,
    additionalData: any[],
    rowNumber: number,
    numberOfColumns: number,
    widthRow: any[],
    centerAlignAdditionalData?
  ) {
    if (additionalData.length > 0) {
      for (const data of additionalData) {
        worksheet.addRow(data).eachCell((c, i) => {
          this.applyFormatsToAdditionalData(c, 10, i % 2 === 0 ? false : true);
          const existingWidth = widthRow[i - 1];
          widthRow[i - 1] = existingWidth > c.value.toString().length ? existingWidth : c.value.toString().length;
        });
        if (data.length < numberOfColumns) {
          if (centerAlignAdditionalData) {
            worksheet.mergeCells(rowNumber, data.length - 1, rowNumber, numberOfColumns / 2);
            worksheet.mergeCells(rowNumber, numberOfColumns / 2 + 1, rowNumber, numberOfColumns);
            const leftCell = worksheet.getCell(rowNumber, 1);
            leftCell.alignment = { horizontal: 'right' };
            const cell = worksheet.getCell(rowNumber, numberOfColumns / 2 + 1);
            cell.value = data[data.length - 1];
            this.applyFormatsToAdditionalData(cell, 10, false);
          } else {
            worksheet.mergeCells(rowNumber, data.length, rowNumber, numberOfColumns);
          }
        }
        rowNumber++;
      }
    }
    return { rowNumber, widthRow };
  }

  /**
   * apply formatting to additional data
   * @param cell cell
   * @param fontSize font size
   * @param isBold indicate if font is bold
   */
  private applyFormatsToAdditionalData(cell: Cell, fontSize: number, isBold) {
    cell.font = { bold: isBold, size: fontSize };

    this.applyBorderToCell(cell);

    this.applyBackGroundToCell(cell, 'FFFFE0');
  }

  /**
   * get number of columns the excel has
   * @param headerRow headerRow
   * @param additionalData additionalData
   */
  private getNumberOfColumns(headerRow, additionalData) {
    let numberOfColumns = headerRow.length;

    for (const data of additionalData) {
      numberOfColumns = numberOfColumns > data.length ? numberOfColumns : data.length;
    }

    return numberOfColumns;
  }

  /**
   * export data to excel
   * @param txnMsgControlIdList list of transaction ids and msgControlID list
   * @param summaryAuditReportList summaryAuditReportList
   * @param facilityName facility Name
   * @param env environment
   * @param facilityId facilityId
   */
  private exportSummaryAuditReport(txnMsgControlIdList, summaryAuditReportList, facilityName, env, facilityId) {
    // create workbook
    const workbook = new Workbook();

    // create worksheet
    const worksheet = workbook.addWorksheet();

    // header row with 'text' attribute from the headers
    const headerRow = ['Position', 'Param Name', 'Error Message'];

    // width for each column
    const widthRow = [8, 9, 13];

    let rowNumber = 1;

    // add default title
    this.addHeader(worksheet, 3 + txnMsgControlIdList.length, 'New Jersey Immunization Information System', rowNumber, 18);

    rowNumber++;

    this.addHeader(worksheet, 3 + txnMsgControlIdList.length, `HL7 Summary Audit Report for ${facilityName} (${env} - ${facilityId})`, rowNumber, 16);

    rowNumber++;

    const transactionIdRow = ['', '', ''];

    const msgControlIdRow = ['', '', ''];

    txnMsgControlIdList.forEach(h => {
      transactionIdRow.push(h.transactionId);
      msgControlIdRow.push(h.messageCtrId);
      headerRow.push('Severity');
      widthRow.push(10);
    });

    // add header Row
    const cellHeader = worksheet.addRow(transactionIdRow);

    // Cell Style : Fill and Border
    this.applyHeaderFormattingToRowCell(cellHeader);

    // merge cells
    worksheet.mergeCells(rowNumber, 1, rowNumber, 3);

    const transactionIdCell = worksheet.getCell(`A${rowNumber}`);
    transactionIdCell.value = 'Transaction Id';
    transactionIdCell.alignment = { horizontal: 'right' };

    rowNumber++;

    // add header Row
    const msgCellHeader = worksheet.addRow(msgControlIdRow);

    // Cell Style : Fill and Border
    this.applyHeaderFormattingToRowCell(msgCellHeader);

    // merge cells
    worksheet.mergeCells(rowNumber, 1, rowNumber, 3);

    const msgControlIdCell = worksheet.getCell(`A${rowNumber}`);
    msgControlIdCell.value = 'Message Control Id';
    msgControlIdCell.alignment = { horizontal: 'right' };

    rowNumber++;

    const addedHeaderRow = worksheet.addRow(headerRow);

    this.applyHeaderFormattingToRowCell(addedHeaderRow);
    // data row for the excel
    const dataRow = [];

    for (const data of summaryAuditReportList) {
      let cells = [];

      cells.push(data.position);

      widthRow[0] = widthRow[0] > data.position.length ? widthRow[0] : data.position.length;

      cells.push(data.parameterName);

      widthRow[1] = widthRow[1] > data.parameterName.length ? widthRow[1] : data.parameterName.length;

      data.errors.forEach((error, i) => {
        cells.push(error.errorMessage);

        widthRow[2] = widthRow[2] > error.errorMessage.length ? widthRow[2] : error.errorMessage.length;

        const severityArr = [];
        for (const txnAndMsgControlId of txnMsgControlIdList) {
          severityArr.push('');
        }
        const errorTransactionList = error.transactions;
        for (const errTxn of errorTransactionList) {
          txnMsgControlIdList.forEach((txnAndMsgControlId, i) => {
            if (errTxn.transactionId === txnAndMsgControlId.transactionId && errTxn.messageCtrId === txnAndMsgControlId.messageCtrId) {
              severityArr[i] = error.severity;
            }
          });
        }
        for (const severity of severityArr) {
          cells.push(severity);
        }
        if (data.errors.length > 1 && i + 1 < data.errors.length) {
          dataRow.push(cells);
          cells = [];
          cells.push('');
          cells.push('');
        }
      });
      dataRow.push(cells);
    }

    // set width to the column
    worksheet.columns.forEach((column, i) => {
      column.width = widthRow[i] + 1;
    });

    // add dataRow
    dataRow.forEach((d, i) => {
      const bgcolor = i % 2 === 0 ? 'EDEDED' : 'FFFFFF';
      worksheet.addRow(d).eachCell((c, i) => {
        this.applyBackGroundToCell(c, bgcolor);
        this.applyBorderToCell(c);
        c.font = { size: 10 };
        c.alignment = { wrapText: true, vertical: 'middle' };
        if (i > 3) {
          c.alignment.horizontal = 'center';
        }
      });
    });

    /*worksheet.eachRow((r, i) => {
      r.eachCell((c, j) => {
        if (c.value === 'merge') {
          c.merge(worksheet.getRow(i - 1).getCell(j), false);
        }
      });
    });*/
    // save file
    workbook.xlsx.writeBuffer().then(excelData => {
      const date = this.helperService.convertDate(new Date(), 'DD_MMM_YYYY_HH_mm_ss');
      const downloadFileName = 'Summary_Audit_Report';
      const blob = new Blob([excelData], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, `${downloadFileName}_${facilityId}_${date}.xlsx`);
    });
  }

  /**
   * apply formatting to cell in a row
   * @param row row
   */
  private applyHeaderFormattingToRowCell(row: Row) {
    row.eachCell((cell, i) => {
      cell.font = { bold: true, size: 10 };
      this.applyBackGroundToCell(cell, 'C6E0B4');
      this.applyBorderToCell(cell);
    });
  }

  /**
   * export data to excel file
   * @param title title of the excel
   * @param data data to be exported
   * @param headers headers for the data
   * @param additionalData additional data to be included above the data
   * @param subTitle subTitle for the excel
   * @param fileName fileName for the excel to be downloaded
   */
  getExcelBlob(title: string, data: any[], headers: any[], additionalData: any[], subTitle: string, fileName?: string) {
    // create workbook
    const workbook = new Workbook();

    // create worksheet
    const worksheet = workbook.addWorksheet();

    // header row with 'text' attribute from the headers
    const headerRow = [];

    // width for each column
    let widthRow = [];

    // for header list, create headerRow and mergedCells data
    headers.forEach((h, i) => {
      if (this.helperService.isHeaderPartOfExport(h)) {
        headerRow.push(h.text);
        widthRow.push(h.text.length);
      }
    });

    const numberOfColumns = this.getNumberOfColumns(headerRow, additionalData);

    let rowNumber = 1;

    // add default title
    this.addHeader(worksheet, numberOfColumns, 'New Jersey Immunization Information System', rowNumber, 18);

    rowNumber++;

    // add title for the excel
    if (!this.helperService.IsNullOrEmpty(title)) {
      this.addHeader(worksheet, numberOfColumns, title, rowNumber, 16);
      rowNumber++;
    }

    if (!this.helperService.IsNullOrEmpty(subTitle)) {
      this.addHeader(worksheet, headerRow.length, subTitle, rowNumber, 16);
      rowNumber++;
    }

    // add additional data
    const obj = this.addAdditionalData(worksheet, additionalData, rowNumber, numberOfColumns, widthRow);

    rowNumber = obj.rowNumber;

    widthRow = obj.widthRow;

    // Blank Row
    worksheet.addRow([]);

    // add header Row
    const cellHeader = worksheet.addRow(headerRow);

    // Cell Style : Fill and Border
    cellHeader.eachCell(cell => {
      cell.font = {
        bold: true,
        size: 10
      };
      this.applyBackGroundToCell(cell, 'C6E0B4');
      this.applyBorderToCell(cell);
    });

    // data row for the excel
    const dataRow = [];

    for (const d of data) {
      const cells = [];
      let i = 0;
      headers.forEach(h => {
        if (this.helperService.isHeaderPartOfExport(h)) {
          const cellValue =
            h.format === 'PHONE' ? this.helperService.formatPhone(d[h.field]) : d[h.field]?.replace(/\n/g, '\r\n').replace(/<br\/>/g, '\n');
          cells.push(cellValue);
          if (cellValue) {
            const existingWidth = widthRow[i];
            widthRow[i] = existingWidth > cellValue.length ? existingWidth : cellValue.length;
          }
          i++;
        }
      });
      dataRow.push(cells);
    }

    // set width to the column
    worksheet.columns.forEach((column, i) => {
      column.width = widthRow[i] + 1;
    });

    // add dataRow
    dataRow.forEach((d, i) => {
      const bgcolor = i % 2 === 0 ? 'EDEDED' : 'FFFFFF';
      worksheet.addRow(d).eachCell(c => {
        this.applyBackGroundToCell(c, bgcolor);
        this.applyBorderToCell(c);
        c.font = {
          size: 10
        };
        c.alignment = {
          wrapText: true,
          vertical: 'middle'
        };
      });
    });

    // save file
    return workbook.xlsx.writeBuffer();
  }
}
